import { supabase } from "@/integrations/supabase/client";

export type ThumbSize = { width: number; height?: number; quality?: number; resize?: "cover" | "contain" | "fill" };

/**
 * Returns a signed URL for a photo in the `business-photos` bucket, with
 * Supabase image transform (resize + quality) applied so the browser
 * downloads a small thumbnail instead of the full original.
 */
export async function getSignedThumb(
  path: string,
  { width, height, quality = 70, resize = "cover" }: ThumbSize,
  expiresIn = 3600,
): Promise<string | null> {
  const { data } = await supabase.storage
    .from("business-photos")
    .createSignedUrl(path, expiresIn, {
      transform: { width, height, quality, resize },
    });
  return data?.signedUrl ?? null;
}

/** Batch helper keyed by an arbitrary id. */
export async function getSignedThumbs<T extends { id: string; storage_path: string }>(
  items: T[],
  size: ThumbSize,
  expiresIn = 3600,
): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  await Promise.all(
    items.map(async (it) => {
      const url = await getSignedThumb(it.storage_path, size, expiresIn);
      if (url) out[it.id] = url;
    }),
  );
  return out;
}
